import { h, Component } from 'preact';
import { FlexCenterStretched } from '../../utils/emotion';
import { Controls, DisplayMode, MediaSymbolTypes, VideoPlayerType } from '../../typing/enums';
import { withContext } from '../../common/context';
import { css } from 'react-emotion';
import Placeholder from '../Placeholder/Placeholder';
import { Events } from '../../utils/events';
import { isMobile } from 'mobile-device-detect';
import { getAdjustedDims, checkSameRatio } from '../../utils/assetHelpers';

import { LazyComponent } from '../LazyComponent/LazyComponent';

const CldVideoPlayer = LazyComponent(
  () => import(/* webpackChunkName: "cld-video-player" */ './CldVideoPlayer/CldVideoPlayer')
);
const NativeVideoPlayer = LazyComponent(
  () =>
    import(/* webpackChunkName: "native-video-player" */ './NativeVideoPlayer/NativeVideoPlayer')
);

class Video extends Component {
  progress = 0;

  state = {
    isReady: false
  };

  get controlsType() {
    return `${this.props.context.config.selectVideoPropsControls()}`;
  }

  get isNativeControls() {
    return this.controlsType === Controls.ALL;
  }

  get isMinimalControls() {
    return (
      this.controlsType === Controls.DEPRECATED_CONTROLS_ON || this.controlsType === Controls.PLAY
    );
  }

  get isControls() {
    return this.isMinimalControls || this.isNativeControls;
  }

  get loopProp() {
    return this.props.context.config.selectVideoPropsLoop();
  }

  get isAutoPlay() {
    return this.props.context.config.selectVideoPropsAutoplay() || !this.isControls;
  }

  get isLoop() {
    return this.loopProp || !this.isControls;
  }

  get playSound() {
    return this.props.context.config.selectVideoPropsSound();
  }

  get dims() {
    const { width, height, breakpoint } = this.props;

    return getAdjustedDims(width, height, breakpoint);
  }

  get isMuted() {
    const mode = this.props.context.config.selectDisplayPropsMode();
    const isAutoplayAndFirst = this.props.isFirst && this.isAutoPlay;

    return isMobile || isAutoplayAndFirst || mode === DisplayMode.EXPANDED || !this.playSound;
  }

  getIsSameRatio = prevProps => {
    return checkSameRatio(this.dims.width, this.dims.height, prevProps.width, prevProps.height);
  };

  shouldAutoPlay = (prevProps, player) => {
    if (!prevProps.isReady && this.props.active && this.isAutoPlay) {
      player.play();
    } else if (!this.props.active) {
      player.stop();
    } else if (
      this.getIsSameRatio(prevProps) &&
      this.props.active !== prevProps.active &&
      this.isAutoPlay
    ) {
      player.play();
    }
  };

  onReady = isReady => {
    this.setState({ isReady });
  };

  onPlay = () => {
    this.props.context.events(Events.VIDEO_START, this.props.publicId);
  };

  onPause = () => {
    if (this.props.active) {
      this.props.context.events(Events.VIDEO_PAUSE, this.props.publicId);
    }
  };

  onTimeProgress = progress => {
    this.props.context.events(Events.VIDEO_PROGRESS, this.props.publicId, { progress });
  };

  renderLoading = () => {
    return (
      <FlexCenterStretched
        absolute
        className={css({ alignItems: 'start' })}
        data-test="video-loading-wrap"
      >
        <Placeholder
          publicId={this.props.publicId}
          width={this.dims.width}
          height={this.dims.height}
          mediaType={MediaSymbolTypes.VIDEO}
          transformation={this.props.transformation}
        />
      </FlexCenterStretched>
    );
  };

  render(props, state) {
    const config = this.props.context.config.selectConfig();
    const { playerType } = config.videoProps;

    return (
      <FlexCenterStretched relative data-test="video-wrap">
        {playerType === VideoPlayerType.CLD ? (
          <CldVideoPlayer
            videoPlayerSource={props.videoPlayerSource}
            width={this.dims.width}
            height={this.dims.height}
            ariaLabel={props.ariaLabel}
            publicId={props.publicId}
            transformation={props.transformation}
            context={props.context}
            active={props.active}
            isReady={state.isReady}
            isAutoPlay={this.isAutoPlay}
            onReady={this.onReady}
            isControls={this.isControls}
            isNativeControls={this.isNativeControls}
            isMinimalControls={this.isMinimalControls}
            isMuted={this.isMuted}
            isLoop={this.isLoop}
            loadingComponent={this.renderLoading}
            onPlay={this.onPlay}
            onPause={this.onPause}
            shouldAutoPlay={this.shouldAutoPlay}
            controlsType={this.controlsType}
            onTimeProgress={this.onTimeProgress}
            assetTransformation={props.assetTransformation}
          />
        ) : (
          <NativeVideoPlayer
            {...props}
            width={this.dims.width}
            height={this.dims.height}
            isReady={state.isReady}
            isAutoPlay={this.isAutoPlay}
            loadingComponent={this.renderLoading}
            onReady={this.onReady}
            onPlay={this.onPlay}
            onPause={this.onPause}
            isControls={this.isControls}
            isLoop={this.isLoop}
            isMuted={this.isMuted}
            isMinimalControls={this.isMinimalControls}
            isNativeControls={this.isNativeControls}
            onTimeProgress={this.onTimeProgress}
            shouldAutoPlay={this.shouldAutoPlay}
            getIsSameRatio={this.getIsSameRatio}
          />
        )}
      </FlexCenterStretched>
    );
  }
}

export default withContext(Video);
